/**
 * InfiniteScrollFooter — the end-of-list slot that keeps a paginated list
 * loading itself.
 *
 * Rendered after the last row for as long as the server has another page. The
 * slot keeps a constant height whether or not a request is running, so the list
 * never shifts under a member who is mid-scroll; only the spinner's opacity
 * changes. `useInfiniteScroll` starts the request about a screen and a half
 * early, so the rows normally arrive before the member scrolls this far.
 *
 * Render it in place of, never alongside, a pagination failure: that message
 * owns the retry (see ListStatus) and auto-loading must not fight it.
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useInfiniteScroll } from '../../utils/useInfiniteScroll';
import { Button } from './Button';
import './infiniteScrollFooter.css';

export interface InfiniteScrollFooterProps {
  hasNextPage: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  /** Rows currently rendered — lets the hook notice a page that added nothing. */
  itemCount: number;
  className?: string;
}

export function InfiniteScrollFooter({
  hasNextPage,
  isFetchingMore,
  onLoadMore,
  itemCount,
  className = '',
}: InfiniteScrollFooterProps) {
  const { t } = useTranslation();
  const { sentinelRef, needsManualTrigger, loadMore } = useInfiniteScroll({
    hasNextPage,
    isFetching: isFetchingMore,
    onLoadMore,
    itemCount,
  });

  if (!hasNextPage) return null;

  const classes = ['infinite-footer', isFetchingMore ? 'infinite-footer--busy' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <div ref={sentinelRef} className="infinite-footer__sentinel" aria-hidden />
      {needsManualTrigger ? (
        <Button size="sm" variant="outline" onClick={loadMore} disabled={isFetchingMore}>
          {t(isFetchingMore ? 'common.pagination.loadingMore' : 'common.pagination.loadMore')}
        </Button>
      ) : (
        <span className="infinite-footer__spinner" aria-hidden />
      )}
      <span className="sr-only" role="status">
        {isFetchingMore ? t('common.pagination.loadingMore') : ''}
      </span>
    </div>
  );
}
